import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getUserApartmentId } from '@/lib/apartment';

export interface BuildingPricing {
  id: string;
  name: string;
  rate_per_kwh: number;
  currency: string;
  peak_rate_per_kwh: number | null;
  off_peak_rate_per_kwh: number | null;
}

/**
 * Hook to fetch building-specific pricing settings
 *
 * Returns the pricing configuration for the user's building.
 * Falls back to default rate (400 TSh/kWh) if no pricing found.
 *
 * @param buildingId - Optional building ID. If not provided, uses user's building.
 *
 * @example
 * ```tsx
 * const { pricing, isLoading } = usePricing();
 * const ratePerKwh = pricing?.rate_per_kwh ?? 400;
 * ```
 */
export function usePricing(buildingId?: string) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['pricing', buildingId ?? user?.id],
    queryFn: async () => {
      let targetBuildingId = buildingId;

      // If no building ID provided, get user's building (supports cotenants)
      if (!targetBuildingId && user?.id) {
        const apartmentId = await getUserApartmentId(user.id);

        if (!apartmentId) {
          console.error('Failed to fetch user apartment');
          return null;
        }

        // Get building ID from apartment
        const { data: apartmentData, error: aptError } = await supabase
          .from('apartments')
          .select('building_id')
          .eq('id', apartmentId)
          .single();

        if (aptError || !apartmentData?.building_id) {
          console.error('Failed to fetch apartment building:', aptError);
          return null;
        }

        targetBuildingId = apartmentData.building_id;
      }

      if (!targetBuildingId) {
        console.warn('No building ID available for pricing lookup');
        return null;
      }

      // Fetch building with pricing
      const { data, error } = await supabase
        .from('buildings')
        .select('id, name, rate_per_kwh, currency, peak_rate_per_kwh, off_peak_rate_per_kwh')
        .eq('id', targetBuildingId)
        .single();

      if (error) {
        console.error('Failed to fetch building pricing:', error);
        return null;
      }

      return data as BuildingPricing;
    },
    enabled: !!buildingId || !!user?.id,
    staleTime: 30 * 1000, // Cache for 30 seconds - allows quicker updates when admin changes rates
    refetchOnWindowFocus: true, // Refetch when user returns to the app
    retry: 2,
  });

  return {
    pricing: query.data,
    buildingId: buildingId,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to get just the rate per kWh with fallback
 *
 * @param fallbackRate - Default rate to use if pricing not found (default: 400)
 * @returns Rate per kWh as number
 *
 * @example
 * ```tsx
 * const ratePerKwh = usePricingRate();
 * const cost = consumption * ratePerKwh;
 * ```
 */
export function usePricingRate(fallbackRate: number = 400): number {
  const { pricing } = usePricing();
  return pricing?.rate_per_kwh ?? fallbackRate;
}

/**
 * Hook to format currency based on organization settings
 *
 * @example
 * ```tsx
 * const formatCurrency = useCurrencyFormatter();
 * const formatted = formatCurrency(1234.56); // "TSh 1,234.56"
 * ```
 */
export function useCurrencyFormatter() {
  const { pricing } = usePricing();
  const currency = pricing?.currency ?? 'TSH';

  return (amount: number): string => {
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency} ${formatted}`;
  };
}
